import {
  extractDataTrackIdFromXpath,
  getElementByXpath,
  getStableXpath,
  isElementVisible,
} from './xpath';
import type {
  TrackingAncestorFingerprint,
  TrackingElementFingerprint,
  TrackingLocatorTrigger,
} from './runtime';
import { isStableFingerprintClassToken } from './runtime';
import { logger } from './logger';

const ELEMENT_NODE_TYPE = 1;
const OVERLAY_CLASS_NAME = 'visual-picker-overlay';
const HIGHLIGHT_OVERLAY_CLASS_NAME =
  'visual-picker-overlay visual-picker-overlay--highlight';
const TRACKED_MARKER_CLASS_NAME =
  'visual-picker-overlay visual-picker-overlay--tracked';
const INVALID_MARKER_CLASS_NAME =
  'visual-picker-overlay visual-picker-overlay--invalid';
const MARKER_STYLE_ID = 'visual-tracking-marker-style';
const OVERLAY_Z_INDEX = '2147483647';
const MARKER_Z_INDEX = '2147483646';
const DEFAULT_HIGHLIGHT_DURATION_MS = 3000;
const FINGERPRINT_ATTRIBUTE_NAMES = [
  'data-track-id',
  'data-testid',
  'id',
  'aria-label',
  'name',
];
const FINGERPRINT_ANCESTOR_DEPTH = 3;
const FINGERPRINT_CLASS_LIMIT = 6;

export interface ElementPickerPayload {
  xpath: string;
  tagName: string;
  text: string;
  fingerprint?: TrackingElementFingerprint;
  rect: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  /** 侧栏 / 顶栏等 Layout 壳上的元素，应绑成全局点 */
  inGlobalChrome?: boolean;
  /** 下拉选项、弹层按钮等，收起后会从页面消失 */
  inOverlay?: boolean;
  trigger?: TrackingLocatorTrigger;
}

export interface ElementPickerOptions {
  root?: Document;
  onSelect: (payload: ElementPickerPayload, element: Element) => void;
}

export interface ElementPickerController {
  destroy: () => void;
}

export interface HighlightElementOptions {
  root?: Document;
  xpath: string;
  durationMs?: number;
}

export type TrackedMarkerStatus = 'tracked' | 'invalid';

export interface TrackedMarkerItem {
  xpath: string;
  status: TrackedMarkerStatus;
  tagName?: string;
  text?: string;
}

export interface SyncTrackedMarkersOptions {
  root?: Document;
  markers: TrackedMarkerItem[];
}

interface MarkerOverlayItem {
  overlay: HTMLElement;
  element: Element;
  marker: TrackedMarkerItem;
}

interface MarkerLayerState {
  root: Document;
  markers: TrackedMarkerItem[];
  items: MarkerOverlayItem[];
  onReposition: () => void;
  unbindReposition?: () => void;
  observer?: MutationObserver;
  refreshTimer?: number;
}

function isElement(value: unknown): value is Element {
  return Boolean(
    value &&
      typeof value === 'object' &&
      (value as Element).nodeType === ELEMENT_NODE_TYPE
  );
}

function getTargetElement(target: EventTarget | null): Element | null {
  if (isElement(target)) {
    return target;
  }

  const parent = (target as Node | null)?.parentElement;
  return isElement(parent) ? parent : null;
}

function createEmptyPickerController(): ElementPickerController {
  return {
    destroy() {
      return undefined;
    },
  };
}

function createOverlay(root: Document): HTMLElement | null {
  if (!root.documentElement || typeof root.createElement !== 'function') {
    return null;
  }

  const overlay = root.createElement('div');
  overlay.className = OVERLAY_CLASS_NAME;
  overlay.style.position = 'fixed';
  overlay.style.display = 'none';
  overlay.style.pointerEvents = 'none';
  overlay.style.border = '2px solid #1677ff';
  overlay.style.background = 'rgba(22, 119, 255, 0.08)';
  overlay.style.boxSizing = 'border-box';
  overlay.style.zIndex = OVERLAY_Z_INDEX;

  root.documentElement.appendChild(overlay);
  return overlay;
}

function createHighlightOverlay(root: Document): HTMLElement | null {
  const overlay = createOverlay(root);

  if (!overlay) {
    return null;
  }

  overlay.className = HIGHLIGHT_OVERLAY_CLASS_NAME;
  overlay.style.border = '2px solid #d46b08';
  overlay.style.background = 'rgba(212, 107, 8, 0.2)';
  overlay.style.boxShadow = '0 0 0 4px rgba(212, 107, 8, 0.22)';

  return overlay;
}

function isScrollableOverflow(style: CSSStyleDeclaration): boolean {
  const overflowY = style.overflowY;
  const overflowX = style.overflowX;
  return (
    overflowY === 'clip' ||
    overflowX === 'clip' ||
    overflowY === 'auto' ||
    overflowY === 'scroll' ||
    overflowY === 'hidden' ||
    overflowX === 'auto' ||
    overflowX === 'scroll' ||
    overflowX === 'hidden'
  );
}

function getOverflowAncestors(element: Element): Element[] {
  const ancestors: Element[] = [];
  const doc = element.ownerDocument;
  const win = doc?.defaultView;
  if (!win) {
    return ancestors;
  }

  let current = element.parentElement;
  while (current && current !== doc.documentElement) {
    try {
      if (isScrollableOverflow(win.getComputedStyle(current))) {
        ancestors.push(current);
      }
    } catch (error) {
      break;
    }
    current = current.parentElement;
  }
  return ancestors;
}

function intersectRects(
  a: { left: number; top: number; width: number; height: number },
  b: { left: number; top: number; width: number; height: number }
): { left: number; top: number; width: number; height: number } | null {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.left + a.width, b.left + b.width);
  const bottom = Math.min(a.top + a.height, b.top + b.height);
  const width = right - left;
  const height = bottom - top;
  if (width < 1 || height < 1) {
    return null;
  }
  return { left, top, width, height };
}

/** 相对视口与 overflow 祖先裁剪，滚出可视区则不画框 */
function getClippedOverlayRect(
  element: Element
): { left: number; top: number; width: number; height: number } | null {
  if (!element.isConnected || !isElementVisible(element)) {
    return null;
  }
  const win = element.ownerDocument.defaultView;
  if (!win) {
    return null;
  }

  const box = element.getBoundingClientRect();
  let rect: {
    left: number;
    top: number;
    width: number;
    height: number;
  } | null = {
    left: box.left,
    top: box.top,
    width: box.width,
    height: box.height,
  };
  rect = intersectRects(rect, {
    left: 0,
    top: 0,
    width: win.innerWidth,
    height: win.innerHeight,
  });
  if (!rect) {
    return null;
  }

  const ancestors = getOverflowAncestors(element);
  for (let index = 0; index < ancestors.length; index += 1) {
    const ancestor = ancestors[index];
    const clip = ancestor.getBoundingClientRect();
    rect = intersectRects(rect, {
      left: clip.left + ancestor.clientLeft,
      top: clip.top + ancestor.clientTop,
      width: ancestor.clientWidth,
      height: ancestor.clientHeight,
    });
    if (!rect) {
      return null;
    }
  }

  // 弹窗/抽屉等前景盖住目标时不画框，避免「已埋」叠在遮罩上
  if (
    isForegroundCoveringPoint(
      element,
      rect.left + rect.width / 2,
      rect.top + rect.height / 2
    )
  ) {
    return null;
  }
  return rect;
}

function bindOverlayReposition(
  root: Document,
  elements: Element[],
  onReposition: () => void
): () => void {
  const win = root.defaultView;
  const targets = new Set<EventTarget>();
  targets.add(root);
  if (win) {
    targets.add(win);
  }
  elements.forEach((element) => {
    getOverflowAncestors(element).forEach((ancestor) => {
      targets.add(ancestor);
    });
  });

  targets.forEach((target) => {
    target.addEventListener('scroll', onReposition, true);
  });
  win?.addEventListener('resize', onReposition);

  return () => {
    targets.forEach((target) => {
      target.removeEventListener('scroll', onReposition, true);
    });
    win?.removeEventListener('resize', onReposition);
  };
}

function isForegroundCoveringPoint(
  element: Element,
  x: number,
  y: number
): boolean {
  const doc = element.ownerDocument;
  if (!doc || typeof doc.elementsFromPoint !== 'function') {
    return false;
  }

  const stack = doc.elementsFromPoint(x, y);
  for (let index = 0; index < stack.length; index += 1) {
    const hit = stack[index];
    if (isOverlayElement(hit, null)) {
      continue;
    }
    return !(element === hit || element.contains(hit) || hit.contains(element));
  }
  return false;
}

function isOverlayElement(element: Element, overlay: Element | null): boolean {
  if (overlay && (element === overlay || overlay.contains(element))) {
    return true;
  }
  let current: Element | null = element;
  while (current) {
    if (
      typeof current.className === 'string' &&
      current.className.indexOf(OVERLAY_CLASS_NAME) !== -1
    ) {
      return true;
    }
    current = current.parentElement;
  }
  return false;
}

let markerLayerState: MarkerLayerState | null = null;

interface HighlightLayerState {
  root: Document;
  overlay: HTMLElement;
  element: Element;
  onReposition: () => void;
  unbindReposition?: () => void;
  timers: number[];
  rafId: number;
}

let highlightLayerState: HighlightLayerState | null = null;

function clearHighlightOverlay(): void {
  if (!highlightLayerState) {
    return;
  }
  const { overlay, unbindReposition, timers, rafId } = highlightLayerState;
  unbindReposition?.();
  const win = highlightLayerState.root.defaultView;
  if (win) {
    win.cancelAnimationFrame(rafId);
  }
  timers.forEach((timer) => {
    window.clearTimeout(timer);
  });
  removeOverlay(overlay);
  highlightLayerState = null;
}

function bindHighlightOverlay(
  root: Document,
  overlay: HTMLElement,
  element: Element,
  durationMs: number
): void {
  clearHighlightOverlay();

  const onReposition = () => {
    updateOverlay(overlay, element);
  };

  onReposition();
  const unbindReposition = bindOverlayReposition(root, [element], onReposition);

  let frameCount = 0;
  const maxFrames = 90;
  let rafId = 0;
  const tick = () => {
    if (!highlightLayerState) {
      return;
    }
    onReposition();
    frameCount += 1;
    if (frameCount < maxFrames) {
      rafId = window.requestAnimationFrame(tick);
      highlightLayerState.rafId = rafId;
    }
  };
  rafId = window.requestAnimationFrame(tick);

  const timers: number[] = [];
  if (durationMs > 0) {
    timers.push(
      window.setTimeout(() => {
        clearHighlightOverlay();
      }, durationMs)
    );
  }

  highlightLayerState = {
    root,
    overlay,
    element,
    onReposition,
    unbindReposition,
    timers,
    rafId,
  };
}

function ensureMarkerStyles(root: Document): void {
  if (root.getElementById(MARKER_STYLE_ID)) {
    return;
  }
  const style = root.createElement('style');
  style.id = MARKER_STYLE_ID;
  style.textContent = [
    '.' + OVERLAY_CLASS_NAME + '--tracked{',
    'border:2px solid #2b6de8;',
    'background:rgba(22,119,255,.06);',
    '}',
    '.' + OVERLAY_CLASS_NAME + '--invalid{',
    'border:2px dashed rgba(208,48,80,.7);',
    'background:rgba(208,48,80,.06);',
    '}',
  ].join('');
  root.documentElement.appendChild(style);
}

function resolveMarkerElement(
  root: Document,
  item: TrackedMarkerItem
): Element | null {
  const trackId = extractDataTrackIdFromXpath(item.xpath);
  if (trackId) {
    try {
      const byTrackId = root.querySelector(
        '[data-track-id=' + JSON.stringify(trackId) + ']'
      );
      if (byTrackId) {
        return byTrackId;
      }
    } catch (error) {
      // ignore
    }
  }

  const xpath = typeof item.xpath === 'string' ? item.xpath.trim() : '';
  if (xpath) {
    const byXpath = getElementByXpath(xpath, root);
    if (byXpath && !isDropdownPanel(byXpath)) {
      return byXpath;
    }
  }

  const tagName = item.tagName?.trim();
  const text = item.text?.trim();
  if (!tagName || !text) {
    return null;
  }

  const nodes = root.getElementsByTagName(tagName);
  for (let index = 0; index < nodes.length; index += 1) {
    const element = nodes[index];
    if (isDropdownPanel(element)) {
      continue;
    }
    const elementText = (element.textContent || '').trim();
    if (elementText === text || elementText.indexOf(text) === 0) {
      return element;
    }
  }
  return null;
}

function createMarkerOverlay(
  root: Document,
  status: TrackedMarkerStatus
): HTMLElement | null {
  const overlay = createOverlay(root);
  if (!overlay) {
    return null;
  }
  overlay.className =
    status === 'invalid'
      ? INVALID_MARKER_CLASS_NAME
      : TRACKED_MARKER_CLASS_NAME;
  overlay.style.zIndex = MARKER_Z_INDEX;
  overlay.style.borderRadius = '0';
  overlay.style.boxShadow = 'none';
  overlay.style.overflow = 'hidden';
  if (status === 'invalid') {
    overlay.style.border = '2px dashed rgba(208, 48, 80, 0.7)';
    overlay.style.background = 'rgba(208, 48, 80, 0.06)';
  } else {
    overlay.style.border = '2px solid #2b6de8';
    overlay.style.background = 'rgba(22, 119, 255, 0.06)';
  }
  return overlay;
}

export function clearTrackedMarkers(root?: Document): void {
  const targetRoot =
    root ||
    markerLayerState?.root ||
    (typeof document === 'undefined' ? undefined : document);
  if (!markerLayerState || !targetRoot) {
    return;
  }
  if (markerLayerState.root !== targetRoot) {
    return;
  }

  const win = targetRoot.defaultView;
  markerLayerState.unbindReposition?.();
  markerLayerState.observer?.disconnect();
  if (markerLayerState.refreshTimer) {
    win?.clearTimeout(markerLayerState.refreshTimer);
  }
  markerLayerState.items.forEach((item) => {
    removeOverlay(item.overlay);
  });
  markerLayerState = null;
}

function applyMarkerVisibility(item: MarkerOverlayItem): void {
  updateOverlay(item.overlay, item.element);
}

function rebuildMarkerItems(
  root: Document,
  markers: TrackedMarkerItem[]
): MarkerOverlayItem[] {
  const items: MarkerOverlayItem[] = [];
  markers.forEach((marker) => {
    if (
      !marker ||
      (marker.status !== 'tracked' && marker.status !== 'invalid')
    ) {
      return;
    }
    const element = resolveMarkerElement(root, marker);
    if (!element || !isElementVisible(element)) {
      return;
    }
    const overlay = createMarkerOverlay(root, marker.status);
    if (!overlay) {
      return;
    }
    updateOverlay(overlay, element);
    items.push({ overlay, element, marker });
  });
  return items;
}

export function syncTrackedMarkers(options: SyncTrackedMarkersOptions): void {
  const root =
    options.root || (typeof document === 'undefined' ? undefined : document);
  if (!root) {
    return;
  }

  clearTrackedMarkers(root);
  ensureMarkerStyles(root);

  const markers = Array.isArray(options.markers) ? options.markers : [];
  const items = rebuildMarkerItems(root, markers);

  let rafId = 0;
  const onReposition = () => {
    const win = root.defaultView;
    if (!win || !markerLayerState || markerLayerState.root !== root) {
      return;
    }
    if (rafId) {
      return;
    }
    rafId = win.requestAnimationFrame(() => {
      rafId = 0;
      if (!markerLayerState || markerLayerState.root !== root) {
        return;
      }
      markerLayerState.items.forEach((item) => {
        applyMarkerVisibility(item);
      });
    });
  };

  const refreshFromDom = () => {
    if (!markerLayerState || markerLayerState.root !== root) {
      return;
    }
    markerLayerState.unbindReposition?.();
    markerLayerState.items.forEach((item) => {
      removeOverlay(item.overlay);
    });
    markerLayerState.items = rebuildMarkerItems(root, markerLayerState.markers);
    markerLayerState.unbindReposition = bindOverlayReposition(
      root,
      markerLayerState.items.map((item) => item.element),
      onReposition
    );
  };

  const scheduleRefresh = () => {
    if (!markerLayerState) {
      return;
    }
    const win = root.defaultView;
    if (!win) {
      return;
    }
    if (markerLayerState.refreshTimer) {
      win.clearTimeout(markerLayerState.refreshTimer);
    }
    markerLayerState.refreshTimer = win.setTimeout(() => {
      refreshFromDom();
    }, 120);
  };

  const observer =
    root.body && typeof MutationObserver === 'function'
      ? new MutationObserver(() => {
          onReposition();
          scheduleRefresh();
        })
      : undefined;
  if (observer && root.body) {
    observer.observe(root.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
    });
  }

  markerLayerState = {
    root,
    markers,
    items,
    onReposition,
    unbindReposition: bindOverlayReposition(
      root,
      items.map((item) => item.element),
      onReposition
    ),
    observer,
  };
}

function updateOverlay(overlay: HTMLElement, element: Element): void {
  const rect = getClippedOverlayRect(element);
  if (!rect) {
    overlay.style.display = 'none';
    return;
  }
  overlay.style.display = 'block';
  overlay.style.left = rect.left + 'px';
  overlay.style.top = rect.top + 'px';
  overlay.style.width = rect.width + 'px';
  overlay.style.height = rect.height + 'px';
  const win = element.ownerDocument.defaultView;
  overlay.style.borderRadius = win
    ? win.getComputedStyle(element).borderRadius
    : '0';
}

function removeOverlay(overlay: HTMLElement | null): void {
  if (!overlay || !overlay.parentNode) {
    return;
  }

  try {
    overlay.parentNode.removeChild(overlay);
  } catch (error) {
    logger.error('移除圈选浮层异常', error);
  }
}

const GLOBAL_CHROME_SELECTOR = '[data-tracking-chrome], .arco-layout-sider';
const GLOBAL_CHROME_POPUP_SELECTOR =
  '.arco-dropdown, .arco-trigger-popup, .arco-menu-pop';
const GLOBAL_CHROME_OPEN_TRIGGER_SELECTOR =
  '.arco-dropdown-open, .arco-trigger-open, .arco-menu-open';
const OVERLAY_CONTAINER_SELECTOR = [
  '.arco-select-popup',
  '.arco-tree-select-popup',
  '.arco-cascader-popup',
  '.arco-picker-container',
  '.arco-timepicker',
  '.arco-dropdown',
  '.arco-menu-pop',
  '.arco-trigger-popup',
  '.arco-modal-wrapper',
  '.arco-drawer-wrapper',
  '.arco-mobile-popup',
  '.arco-mobile-masking',
  '.arco-popup',
  '[role="listbox"]',
].join(',');
const OPEN_OVERLAY_TRIGGER_SELECTOR = [
  '.arco-select-open .arco-select-view',
  '.arco-tree-select-open .arco-tree-select-view',
  '.arco-cascader-open .arco-cascader-view',
  '.arco-cascader-open',
  '.arco-picker-open',
  '.arco-dropdown-open',
  '[aria-expanded="true"]',
].join(',');
const OVERLAY_TRIGGER_CONTROL_SELECTOR =
  'select, .arco-select-view, .arco-tree-select-view, .arco-cascader-view, .arco-picker, [role="combobox"]';

function hasOpenTriggerInGlobalChrome(root: Document): boolean {
  const chromes = root.querySelectorAll(GLOBAL_CHROME_SELECTOR);
  for (let i = 0; i < chromes.length; i += 1) {
    if (chromes[i].querySelector(GLOBAL_CHROME_OPEN_TRIGGER_SELECTOR)) {
      return true;
    }
  }
  return false;
}

function isInGlobalChrome(element: Element): boolean {
  if (element.closest(GLOBAL_CHROME_SELECTOR)) {
    return true;
  }
  // 头像「退出登录」等下拉挂到 body，不在顶栏 DOM 里
  if (!element.closest(GLOBAL_CHROME_POPUP_SELECTOR)) {
    return false;
  }
  const root = element.ownerDocument;
  return Boolean(root && hasOpenTriggerInGlobalChrome(root));
}

function findOverlayContainer(element: Element): Element | null {
  return element.closest(OVERLAY_CONTAINER_SELECTOR);
}

function isDropdownPanel(element: Element): boolean {
  try {
    return element.matches(OVERLAY_CONTAINER_SELECTOR);
  } catch (error) {
    return false;
  }
}

function findOverlayTrigger(overlay: Element): Element | null {
  const root = overlay.ownerDocument;
  if (!root) {
    return null;
  }
  const popupId = overlay.id.trim();
  if (popupId && /^[\w-]+$/.test(popupId)) {
    const controlled = root.querySelector('[aria-controls="' + popupId + '"]');
    if (controlled) {
      return controlled.closest(OVERLAY_TRIGGER_CONTROL_SELECTOR) || controlled;
    }
  }
  const openTriggers = root.querySelectorAll(OPEN_OVERLAY_TRIGGER_SELECTOR);
  if (!openTriggers.length) {
    return null;
  }
  if (openTriggers.length === 1) {
    return (
      openTriggers[0].closest(OVERLAY_TRIGGER_CONTROL_SELECTOR) ||
      openTriggers[0]
    );
  }
  for (let index = 0; index < openTriggers.length; index += 1) {
    const trigger = openTriggers[index];
    const control = trigger.closest(OVERLAY_TRIGGER_CONTROL_SELECTOR);
    if (control) {
      return control;
    }
  }
  return openTriggers[0];
}

function createOverlayTrigger(
  element: Element
): TrackingLocatorTrigger | undefined {
  const overlay = findOverlayContainer(element);
  if (!overlay) {
    return undefined;
  }
  const trigger = findOverlayTrigger(overlay);
  if (!trigger || trigger === element) {
    return undefined;
  }
  const xpath = getStableXpath(trigger) || undefined;
  const fingerprint = createElementFingerprint(trigger);
  if (!xpath && !fingerprint) {
    return undefined;
  }
  return {
    xpath,
    fingerprint,
  };
}

function normalizeFingerprintText(value?: string): string {
  return (value || '').replace(/\s+/g, ' ').trim().slice(0, 80);
}

function isStableFingerprintValue(name: string, value: string): boolean {
  if (!value) {
    return false;
  }
  if (name !== 'id') {
    return true;
  }
  return !/^(arco-|rc-|react-|css-|:r)/i.test(value) && !/^\d+$/.test(value);
}

function getFingerprintClassTokens(element: Element): string[] {
  return getElementClassName(element)
    .split(/\s+/)
    .filter(isStableFingerprintClassToken)
    .slice(0, FINGERPRINT_CLASS_LIMIT);
}

function getStableFingerprintAttributes(
  element: Element
): Record<string, string> | undefined {
  const attributes: Record<string, string> = {};
  FINGERPRINT_ATTRIBUTE_NAMES.forEach((name) => {
    const value = (element.getAttribute(name) || '').trim();
    if (isStableFingerprintValue(name, value)) {
      attributes[name] = value;
    }
  });
  return Object.keys(attributes).length ? attributes : undefined;
}

function createAncestorFingerprint(
  element: Element
): TrackingAncestorFingerprint {
  const role = (element.getAttribute('role') || '').trim().toLowerCase();
  const classTokens = getFingerprintClassTokens(element);
  return {
    tagName: element.tagName.toLowerCase(),
    role: role || undefined,
    classTokens: classTokens.length ? classTokens : undefined,
  };
}

function createElementFingerprint(
  element: Element
): TrackingElementFingerprint {
  const role = (element.getAttribute('role') || '').trim().toLowerCase();
  const classTokens = getFingerprintClassTokens(element);
  const ancestorChain: TrackingAncestorFingerprint[] = [];
  let current = element.parentElement;
  while (
    current &&
    ancestorChain.length < FINGERPRINT_ANCESTOR_DEPTH &&
    current.tagName.toLowerCase() !== 'html'
  ) {
    ancestorChain.push(createAncestorFingerprint(current));
    current = current.parentElement;
  }
  return {
    tagName: element.tagName.toLowerCase(),
    role: role || undefined,
    text:
      normalizeFingerprintText(element.textContent || undefined) || undefined,
    href: (element.getAttribute('href') || '').trim() || undefined,
    stableAttributes: getStableFingerprintAttributes(element),
    classTokens: classTokens.length ? classTokens : undefined,
    ancestorChain: ancestorChain.length ? ancestorChain : undefined,
  };
}

function collapseRepeatedText(text: string): string {
  let result = text.replace(/\s+/g, ' ').trim();
  while (result.length >= 4) {
    const half = result.length / 2;
    if (half !== Math.floor(half)) {
      break;
    }
    const left = result.slice(0, half);
    if (left !== result.slice(half)) {
      break;
    }
    result = left;
  }
  return result;
}

function getVisibleElementText(element: Element): string {
  const htmlElement = element as HTMLElement;
  const raw =
    typeof htmlElement.innerText === 'string' && htmlElement.innerText
      ? htmlElement.innerText
      : element.textContent || '';
  return collapseRepeatedText(raw);
}

function createPickerPayload(
  element: Element,
  xpath: string
): ElementPickerPayload {
  const rect = element.getBoundingClientRect();
  const text = getVisibleElementText(element);

  const overlay = findOverlayContainer(element);
  const trigger = overlay ? createOverlayTrigger(element) : undefined;

  return {
    xpath,
    tagName: element.tagName.toLowerCase(),
    text: text.slice(0, 80),
    fingerprint: createElementFingerprint(element),
    rect: {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    },
    inGlobalChrome: isInGlobalChrome(element),
    inOverlay: Boolean(overlay),
    trigger,
  };
}

export function highlightElementByXpath(
  options: HighlightElementOptions
): boolean {
  if (
    !options ||
    typeof options.xpath !== 'string' ||
    options.xpath.trim().length === 0
  ) {
    return false;
  }

  const root =
    options.root || (typeof document === 'undefined' ? undefined : document);

  if (!root) {
    return false;
  }

  try {
    const element = getElementByXpath(options.xpath.trim(), root);

    if (!element) {
      return false;
    }

    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({
        block: 'center',
        inline: 'center',
        behavior: 'smooth',
      });
    }

    const overlay = createHighlightOverlay(root);

    if (!overlay) {
      return false;
    }

    const durationMs =
      typeof options.durationMs === 'number' && options.durationMs >= 0
        ? options.durationMs
        : DEFAULT_HIGHLIGHT_DURATION_MS;

    bindHighlightOverlay(root, overlay, element, durationMs);

    return true;
  } catch (error) {
    logger.error('反向高亮异常', error);
    return false;
  }
}

const PICKABLE_TAGS = ['a', 'button', 'select'];
const PICKABLE_ROLES = [
  'button',
  'link',
  'combobox',
  'option',
  'tab',
  'treeitem',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
];
const BLOCKED_FORM_TAGS = ['input', 'textarea', 'label', 'optgroup'];
const BLOCKED_FORM_CLASS_PREFIXES = [
  'arco-input',
  'arco-textarea',
  'arco-mentions',
];
const PICKABLE_SELECTION_OPTION_SELECTOR = [
  '[role="option"]',
  '[role="treeitem"]',
  '.arco-cascader-list-item',
  '.arco-cascader-list-search-item',
  '.arco-picker-cell',
  '.arco-timepicker-cell',
  '.arco-mobile-picker-column-item',
  '.arco-picker-column-item',
  '.arco-mobile-column-item',
  '.arco-column-item',
  '.arco-mobile-cell',
].join(',');
const PICKABLE_SELECTION_CONTROL_SELECTOR = [
  'select',
  '.arco-select-view',
  '.arco-tree-select-view',
  '.arco-cascader-view',
  '.arco-picker',
  '.arco-picker-input',
  '.arco-radio-button',
  '.arco-mobile-select-item',
  '.arco-mobile-select-item-label',
  '.arco-mobile-picker-header-btn',
  '.arco-picker-header-btn',
  '.arco-mobile-form-item-control',
  '.arco-mobile-button',
  '[role="combobox"]',
].join(',');

function getElementClassName(element: Element): string {
  const className = element.className;
  if (typeof className === 'string') {
    return className;
  }
  if (
    className &&
    typeof (className as { baseVal?: string }).baseVal === 'string'
  ) {
    return (className as { baseVal: string }).baseVal;
  }
  return '';
}

function hasClassPrefix(element: Element, prefix: string): boolean {
  const tokens = getElementClassName(element).split(/\s+/);
  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];
    if (token === prefix || token.indexOf(prefix + '-') === 0) {
      return true;
    }
  }
  return false;
}

function isInsideTable(element: Element): boolean {
  if (element.closest('.arco-pagination-option')) {
    return false;
  }
  let current: Element | null = element;
  while (current && current.nodeType === ELEMENT_NODE_TYPE) {
    const tagName = current.tagName.toLowerCase();
    const role = (current.getAttribute('role') || '').trim().toLowerCase();
    if (
      tagName === 'table' ||
      role === 'table' ||
      role === 'grid' ||
      role === 'treegrid' ||
      hasClassPrefix(current, 'arco-table')
    ) {
      return true;
    }
    current = current.parentElement;
  }
  return false;
}

function isBlockedFormControl(element: Element): boolean {
  const tagName = element.tagName.toLowerCase();
  if (BLOCKED_FORM_TAGS.indexOf(tagName) !== -1) {
    return true;
  }
  for (let index = 0; index < BLOCKED_FORM_CLASS_PREFIXES.length; index += 1) {
    if (hasClassPrefix(element, BLOCKED_FORM_CLASS_PREFIXES[index])) {
      return true;
    }
  }
  return false;
}

function hasOwnPointerCursor(element: Element): boolean {
  const win = element.ownerDocument.defaultView;
  if (!win) {
    return false;
  }
  try {
    if (win.getComputedStyle(element).cursor !== 'pointer') {
      return false;
    }
    const parent = element.parentElement;
    if (!parent) {
      return true;
    }
    // 子节点会继承 cursor，不能把文字/图标当成独立可点
    return win.getComputedStyle(parent).cursor !== 'pointer';
  } catch (error) {
    return false;
  }
}

function isPickableSelf(element: Element): boolean {
  const tagName = element.tagName.toLowerCase();
  if (PICKABLE_TAGS.indexOf(tagName) !== -1) {
    return true;
  }
  const role = (element.getAttribute('role') || '').trim().toLowerCase();
  if (role && PICKABLE_ROLES.indexOf(role) !== -1) {
    return true;
  }
  if (isBlockedFormControl(element)) {
    return false;
  }
  return hasOwnPointerCursor(element);
}

/** 从点击目标上溯到最近可圈选节点 */
function findPickableElement(element: Element): Element | null {
  const option = element.closest(PICKABLE_SELECTION_OPTION_SELECTOR);
  if (option) {
    return option;
  }
  const control = element.closest(PICKABLE_SELECTION_CONTROL_SELECTOR);
  if (control && !isDropdownPanel(control)) {
    return control;
  }
  let current: Element | null = element;
  while (current && current.nodeType === ELEMENT_NODE_TYPE) {
    if (isDropdownPanel(current)) {
      return null;
    }
    if (isPickableSelf(current)) {
      return current;
    }
    if (isBlockedFormControl(current)) {
      return null;
    }
    current = current.parentElement;
  }
  return null;
}

function blurDocumentFocus(root: Document): void {
  const active = root.activeElement;
  if (
    !isElement(active) ||
    active === root.body ||
    active === root.documentElement
  ) {
    return;
  }
  const htmlElement = active as HTMLElement;
  if (typeof htmlElement.blur === 'function') {
    htmlElement.blur();
  }
}

function showPickerHint(root: Document, message: string): void {
  const HINT_ID = 'visual-tracking-picker-hint';
  let hint = root.getElementById(HINT_ID);
  if (!hint) {
    hint = root.createElement('div');
    hint.id = HINT_ID;
    hint.style.position = 'fixed';
    hint.style.left = '50%';
    hint.style.bottom = '24px';
    hint.style.transform = 'translateX(-50%)';
    hint.style.zIndex = OVERLAY_Z_INDEX;
    hint.style.padding = '6px 12px';
    hint.style.borderRadius = '4px';
    hint.style.background = 'rgba(0, 0, 0, 0.72)';
    hint.style.color = '#fff';
    hint.style.fontSize = '12px';
    hint.style.pointerEvents = 'none';
    root.documentElement.appendChild(hint);
  }
  hint.textContent = message;
  hint.style.display = 'block';
  const win = root.defaultView;
  if (!win) {
    return;
  }
  const hintNode = hint;
  const prev = Number(hintNode.getAttribute('data-timer') || 0);
  if (prev) {
    win.clearTimeout(prev);
  }
  const timer = win.setTimeout(() => {
    hintNode.style.display = 'none';
  }, 1600);
  hintNode.setAttribute('data-timer', String(timer));
}

export function initElementPicker(
  options: ElementPickerOptions
): ElementPickerController {
  if (!options || typeof options.onSelect !== 'function') {
    logger.error('圈选器初始化失败：缺少 onSelect 回调');
    return createEmptyPickerController();
  }

  const root =
    options.root || (typeof document === 'undefined' ? undefined : document);

  if (!root || typeof root.addEventListener !== 'function') {
    logger.error('圈选器初始化失败：缺少有效 document');
    return createEmptyPickerController();
  }

  const pickerRoot = root;
  const overlay = createOverlay(pickerRoot);

  function handleMouseMove(event: MouseEvent): void {
    try {
      const element = getTargetElement(event.target);

      if (!element || isOverlayElement(element, overlay)) {
        return;
      }

      if (isInsideTable(element)) {
        if (overlay) {
          overlay.style.display = 'none';
        }
        return;
      }

      const pickable = findPickableElement(element);
      if (!pickable) {
        if (overlay) {
          overlay.style.display = 'none';
        }
        return;
      }

      if (overlay) {
        updateOverlay(overlay, pickable);
      }
    } catch (error) {
      logger.error('圈选浮层更新异常', error);
    }
  }

  function handleMouseDown(event: MouseEvent): void {
    const element = getTargetElement(event.target);
    if (!element || isOverlayElement(element, overlay)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    blurDocumentFocus(pickerRoot);
  }

  function handleClick(event: MouseEvent): void {
    try {
      const element = getTargetElement(event.target);

      if (!element || isOverlayElement(element, overlay)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (isInsideTable(element)) {
        showPickerHint(pickerRoot, '暂不支持圈选表格内元素');
        return;
      }

      const pickable = findPickableElement(element);
      if (!pickable) {
        showPickerHint(pickerRoot, '请选择可点击元素');
        return;
      }

      const xpath = getStableXpath(pickable);

      if (!xpath) {
        return;
      }

      options.onSelect(createPickerPayload(pickable, xpath), pickable);
      blurDocumentFocus(pickerRoot);
    } catch (error) {
      logger.error('圈选元素点击处理异常', error);
    }
  }

  pickerRoot.addEventListener('mousedown', handleMouseDown, true);
  pickerRoot.addEventListener('mousemove', handleMouseMove, true);
  pickerRoot.addEventListener('click', handleClick, true);
  logger.info('圈选器已开启');

  return {
    destroy() {
      try {
        pickerRoot.removeEventListener('mousedown', handleMouseDown, true);
        pickerRoot.removeEventListener('mousemove', handleMouseMove, true);
        pickerRoot.removeEventListener('click', handleClick, true);
        removeOverlay(overlay);
        logger.info('圈选器已关闭');
      } catch (error) {
        logger.error('圈选器销毁异常', error);
      }
    },
  };
}
